import { MicrophoneIcon, VolumeUpIcon } from '@heroicons/react/outline';
import Link from 'next/link';

import styles from './receptionist.module.css';
import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  AgentBubble,
  AgentReply,
  VisitorBubble,
} from '@/components/agents/ChatBubbles';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  Stage,
  tone,
  useCopyGap,
  useOpenAgentDemo,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import {
  BRAND_BG,
  BRAND_SOFT,
  BUSINESS,
  BUSINESS_INITIAL,
  CalendarGlyph,
  CALLER_NAME,
  CALLER_NUMBER,
  PhoneGlyph,
  QUIET,
  SIGNUP_HREF,
  useReceptionistT,
  WAVE,
} from './kit';

export default function ReceptionistHero() {
  const { t } = useReceptionistT();
  const gap = useCopyGap();
  const openDemo = useOpenAgentDemo();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-10 sm:pt-16 lg:px-8'>
        {/* The page's one H1 is its keyword, set small as the last step of
            the breadcrumb; the display line under it is the pitch. */}
        <div
          className={clsxm(
            agents.enter,
            'flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm sm:text-[15px]',
          )}
        >
          <nav aria-label={t('Breadcrumb')}>
            <Link
              href='/chatbots'
              className={clsxm(
                'font-medium transition-colors hover:text-primary-700 dark:hover:text-primary-300',
                QUIET,
              )}
            >
              {t('AI Agents')}
            </Link>
          </nav>
          <span aria-hidden='true' className={clsxm('opacity-60', QUIET)}>
            /
          </span>
          <h1 className='text-sm font-semibold leading-6 text-primary-700 sm:text-[15px] dark:text-primary-300'>
            {t('AI receptionist and answering service')}
          </h1>
        </div>

        <div className='mt-6 grid gap-x-14 gap-y-8 lg:grid-cols-12 lg:items-end'>
          <p
            className={clsxm(
              agents.display,
              agents.enter,
              styles.heroDisplay,
              'text-[2.75rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:col-span-7 lg:text-[5rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            {t('Every call answered.')}
            {gap}
            <span className='text-primary-600 dark:text-primary-400'>
              {t('Even when you can’t pick up.')}
            </span>
          </p>

          <div
            className={clsxm(agents.enter, 'lg:col-span-5 lg:pb-2')}
            style={delay(160)}
          >
            <p
              className={clsxm(
                'text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              {t(
                'Your AI receptionist answers your business line with your greeting, replies from your website and documents, books appointments into Google Calendar and takes messages. Around the clock.',
              )}
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-3'>
              <Link href={SIGNUP_HREF} className={buttonPrimary}>
                {t('Build your agent free')}
                <ArrowGlyph />
              </Link>
              <button
                type='button'
                onClick={openDemo}
                className={buttonSecondary}
              >
                <span className='relative flex h-2 w-2'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                  <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
                </span>
                {t('Talk to our agent')}
              </button>
            </div>
            <p className={clsxm('mt-5 text-sm leading-6', QUIET)}>
              {t(
                'Free to build and test. Phone calls need a paid plan and your own Twilio number.',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8'>
        <HeroStage />
      </div>
    </header>
  );
}

function HeroStage() {
  const { t } = useReceptionistT();

  return (
    <Stage
      variant='indigo'
      halftoneAt='100% 0%'
      className={clsxm(
        agents.enter,
        'px-4 py-8 sm:px-10 sm:py-12 lg:h-[38rem] lg:p-0',
      )}
      style={delay(240)}
    >
      <p className='sr-only'>
        {t(
          'Example: an AI receptionist answers a plumbing company’s phone after hours, offers the caller a time and books the visit into Google Calendar.',
        )}
      </p>
      <div
        aria-hidden='true'
        className='relative mx-auto flex max-w-5xl flex-col items-center gap-5 lg:block lg:h-full'
      >
        <div className='hidden lg:absolute lg:start-6 lg:top-16 lg:block lg:w-[14.5rem] xl:start-2'>
          <div className={clsxm(agents.enter, agents.float)} style={delay(700)}>
            <CallerPhone />
          </div>
        </div>

        <div className='w-full max-w-[24rem] lg:absolute lg:left-1/2 lg:top-10 lg:w-[25rem] lg:max-w-none lg:-translate-x-1/2'>
          <div className={agents.enter} style={delay(380)}>
            <CallConsole />
          </div>
        </div>

        <div className='hidden w-full max-w-[24rem] sm:block lg:absolute lg:end-6 lg:top-[15rem] lg:w-[17rem] xl:end-2'>
          <div className={agents.enter} style={delay(1000)}>
            <CalendarCard />
          </div>
        </div>
      </div>
    </Stage>
  );
}

/** What the caller sees: their phone, connected to the business. */
function CallerPhone() {
  const { t } = useReceptionistT();

  return (
    <div className='rounded-[2.6rem] bg-[#0a0a12] p-2 shadow-[0_40px_80px_-30px_rgba(8,6,40,0.9)] ring-1 ring-white/15'>
      <div className='relative overflow-hidden rounded-[2.15rem] bg-[linear-gradient(180deg,#24224a_0%,#121129_55%,#0c0b1d_100%)] px-5 pb-6 pt-3 text-center text-white'>
        <div
          className='flex items-center justify-between text-[11px] font-semibold tabular-nums'
          dir='ltr'
        >
          <span>{t('9:47')}</span>
          <span className='flex items-center gap-1'>
            <span className='flex items-end gap-[2px]'>
              {[4, 6, 8, 10].map((height) => (
                <span
                  key={height}
                  className='w-[3px] rounded-sm bg-white'
                  style={{ height }}
                />
              ))}
            </span>
            <span className='ms-1 h-[9px] w-[18px] rounded-[3px] ring-1 ring-white/70'>
              <span className='m-[1.5px] block h-[6px] w-[11px] rounded-[1.5px] bg-white' />
            </span>
          </span>
        </div>
        <span className='absolute left-1/2 top-2 h-[18px] w-[72px] -translate-x-1/2 rounded-full bg-black' />

        <p className='mt-9 text-[19px] font-semibold leading-tight'>
          {BUSINESS}
        </p>
        <p className='mt-1 text-[13px] tabular-nums text-white/60'>00:42</p>

        <div className='relative mx-auto mt-9 h-[5.5rem] w-[5.5rem]'>
          <span
            className={clsxm(
              styles.ring,
              'absolute inset-0 rounded-full border border-[#8fe3c6]/60',
            )}
          />
          <span
            className={clsxm(
              styles.ring,
              styles.ringLate,
              'absolute inset-0 rounded-full border border-[#8fe3c6]/60',
            )}
          />
          <span
            className={clsxm(
              'relative flex h-full w-full items-center justify-center rounded-full text-3xl font-semibold text-white shadow-[0_10px_30px_-8px_rgba(17,121,95,0.8)]',
              BRAND_BG,
            )}
          >
            {BUSINESS_INITIAL}
          </span>
        </div>

        <div className='mt-10 grid grid-cols-3 gap-3 px-1'>
          <span className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10'>
            <MicrophoneIcon className='h-5 w-5' />
          </span>
          <span className='mx-auto grid h-12 w-12 grid-cols-3 place-content-center gap-[3px] rounded-full bg-white/10 p-3.5'>
            {Array.from({ length: 9 }, (_, index) => (
              <span key={index} className='h-1 w-1 rounded-full bg-white' />
            ))}
          </span>
          <span className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10'>
            <VolumeUpIcon className='h-5 w-5' />
          </span>
        </div>
        <span className='mx-auto mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#e5484d]'>
          <PhoneGlyph className='h-5 w-5 rotate-[135deg]' />
        </span>
      </div>
    </div>
  );
}

/** What the business sees: the call as it happens, transcribed. */
function CallConsole() {
  const { t } = useReceptionistT();

  return (
    <div className='overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
            BRAND_BG,
          )}
        >
          {BUSINESS_INITIAL}
        </span>
        <div className='min-w-0 flex-1'>
          <p className={clsxm('truncate text-[15px] font-semibold', tone.ink)}>
            {BUSINESS}
          </p>
          <p
            className={clsxm(
              'flex min-w-0 items-center gap-1.5 text-xs',
              QUIET,
            )}
          >
            <PhoneGlyph className='h-3 w-3 flex-none' />
            <span className='truncate'>
              {t('Incoming call')} ·{' '}
              <span dir='ltr' className='tabular-nums'>
                {CALLER_NUMBER}
              </span>
            </span>
          </p>
        </div>
        <span className='flex flex-none items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300'>
          <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500 motion-reduce:animate-none' />
          01:12
        </span>
      </div>

      <div className='flex items-center gap-4 px-5 pt-4'>
        <span
          className={clsxm(
            agents.wave,
            agents.waveCenter,
            'flex h-7 min-w-0 flex-1 items-center gap-[3px] overflow-hidden',
          )}
        >
          {WAVE.map((height, index) => (
            <span
              key={index}
              className={clsxm('w-[3px] flex-none rounded-full', BRAND_BG)}
              style={{
                height: `${Math.max(4, Math.round(height * 0.62))}px`,
                opacity: 0.35 + (height / 42) * 0.65,
                ...delay(index * 60),
              }}
            />
          ))}
        </span>
        <span
          className={clsxm(
            'flex-none rounded-full px-2.5 py-1 text-[11px] font-semibold',
            BRAND_SOFT,
          )}
        >
          {t('After hours')} · {t('9:47 p.m.')}
        </span>
      </div>

      <div className='space-y-3 px-5 py-5 text-[14px] leading-relaxed'>
        <VisitorBubble at={700}>
          {t('Our water heater is leaking. Can someone come tomorrow morning?')}
        </VisitorBubble>
        <AgentReply typingAt={1250} at={2050}>
          <AgentBubble brand={BRAND_BG}>
            {t(
              'Sorry to hear that. A plumber is free tomorrow at 8 a.m. Shall I book it?',
            )}
          </AgentBubble>
        </AgentReply>
        <VisitorBubble at={3050}>
          {t('Yes, please. It’s {{name}}.', { name: CALLER_NAME })}
        </VisitorBubble>
        <AgentReply typingAt={3500} at={4200}>
          <div className='flex max-w-[88%] items-center gap-3 rounded-2xl bg-[#f6f6f9] px-3.5 py-2.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
              <CheckGlyph className='h-3.5 w-3.5' />
            </span>
            <span className='min-w-0'>
              <span
                className={clsxm(
                  'block text-[13px] font-semibold leading-tight',
                  tone.ink,
                )}
              >
                {t('Booked in Google Calendar')}
              </span>
              <span className={clsxm('block truncate text-xs', QUIET)}>
                {t('Tomorrow, 8 a.m.')} · {CALLER_NAME}
              </span>
            </span>
          </div>
        </AgentReply>
        <AgentReply at={4800}>
          <AgentBubble brand={BRAND_BG}>
            {t('You’re all set for 8 a.m. tomorrow. See you then!')}
          </AgentBubble>
        </AgentReply>
      </div>
    </div>
  );
}

/** The owner's calendar the next morning, with the visit the call booked. */
function CalendarCard() {
  const { t } = useReceptionistT();
  const hours = [t('7 a.m.'), t('8 a.m.'), t('9 a.m.'), t('10 a.m.')];
  // One row per hour; events are placed in px from the top of the grid.
  const row = 44;

  return (
    <div className='overflow-hidden rounded-2xl bg-white text-start shadow-[0_30px_70px_-25px_rgba(8,6,40,0.7)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-3 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex min-w-0 items-center gap-2 text-[13px] font-semibold',
            tone.ink,
          )}
        >
          <CalendarGlyph className='h-4 w-4 flex-none text-[#1a73e8]' />
          <span className='truncate'>{t('Google Calendar')}</span>
        </span>
        <span className={clsxm('flex-none text-xs font-medium', QUIET)}>
          {t('Tomorrow')}
        </span>
      </div>

      <div className='relative px-4 py-3'>
        <div className='relative' style={{ height: row * hours.length }}>
          {hours.map((label, index) => (
            <div
              key={label}
              className='absolute inset-x-0 flex items-start gap-3'
              style={{ top: index * row }}
            >
              <span
                className={clsxm(
                  'w-14 flex-none -translate-y-[7px] text-[11px] tabular-nums',
                  QUIET,
                )}
              >
                {label}
              </span>
              <span className='mt-px h-px flex-1 bg-black/[0.06] dark:bg-white/[0.07]' />
            </div>
          ))}

          <div
            className='absolute end-0 start-[4.25rem] rounded-lg bg-[#e8eaf6] px-2.5 py-1 text-[11px] font-medium text-[#3c4361] dark:bg-white/[0.07] dark:text-[#b9bdd8]'
            style={{ top: row * 0.25 + 2, height: row * 0.5 - 4 }}
          >
            <span className='block truncate'>{t('Team briefing')}</span>
          </div>

          <div
            className={clsxm(
              agents.bubble,
              agents.bubbleStart,
              'absolute end-0 start-[4.25rem] overflow-hidden rounded-lg border-s-[3px] border-[#11795f] bg-[#11795f]/10 px-2.5 py-1.5 shadow-[0_0_0_3px_rgba(17,121,95,0.18)] dark:bg-[#11795f]/25',
            )}
            style={{
              top: row + 2,
              height: row * 1.5 - 4,
              ...delay(4300),
            }}
          >
            <span
              className={clsxm(
                'block truncate text-[12px] font-semibold leading-tight',
                tone.ink,
              )}
            >
              {t('Water heater repair')}
            </span>
            <span
              className={clsxm(
                'mt-0.5 block truncate text-[11px] leading-tight',
                tone.body,
              )}
            >
              {CALLER_NAME} · {t('8 to 9:30 a.m.')}
            </span>
          </div>

          <div
            className='absolute end-0 start-[4.25rem] rounded-lg bg-[#e8eaf6] px-2.5 py-1 text-[11px] font-medium text-[#3c4361] dark:bg-white/[0.07] dark:text-[#b9bdd8]'
            style={{ top: row * 3 + 2, height: row * 0.75 - 4 }}
          >
            <span className='block truncate'>{t('Drain cleaning')}</span>
          </div>
        </div>
      </div>

      <p
        className={clsxm(
          'flex items-center gap-1.5 border-t border-black/[0.06] px-4 py-2.5 text-[11px] font-medium dark:border-white/[0.06]',
          QUIET,
        )}
      >
        <PhoneGlyph className='h-3 w-3 flex-none' />
        <span className='truncate'>{t('Booked by your AI receptionist')}</span>
      </p>
    </div>
  );
}
